import React, { useEffect, useContext, useState, useMemo, useRef } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import {
    getCarStatusStyle,
    getLastUpdated,
    getBlockedStatusIcon,
    getBlockedStatusStyle,
    navegateToPositionsReport,
    navegateToTripsReport,
    getCarStatusLegendStyle,
    formatDate,
    capitalizeFirstLetter,
    getTimeElapsed
} from '../../utils'
import TripsContext from '../../contexts/TripsContext'
import { useSelectedVehicles } from '../../hooks/useSelectedVehicles'
import { Vehicle } from './type'
import LiveTooltip from 'live/components/ui/tooltip'

type IProp = {
    vehicle: Vehicle
    hardwareData?: any
    address?: string
    isActive?: boolean
    onClick?(vehicle): void
    showDropdownId: number
    setShowDropdownId(number): void
}

const VehicleCard = ({
    vehicle,
    address,
    hardwareData = undefined,
    isActive,
    onClick,
    showDropdownId,
    setShowDropdownId
}: IProp): JSX.Element => {
    const {
        setIsNavegateToTrips,
        setInfoWindow,
        setVehiclesSearched,
        positionsReportId,
        tripsReportId,
        isNavegateToTrips
    } = useContext(TripsContext)

    const { clearAllSelectedVehicles } = useSelectedVehicles()

    const navegateToTrips = (event, vehicle) => {
        event.preventDefault()
        clearAllSelectedVehicles()
        setInfoWindow(undefined)
        setVehiclesSearched(undefined)
        setIsNavegateToTrips({ vehicle, hardwareData })
    }

    const navegateToVehicle = (event: React.MouseEvent, idVehicle: number): void => {
        // event.preventDefault()
        // const url = `#/config-vehicle/${idVehicle}`
        // window.open(url, '_blank')
    }

    function isWithin24Hours(timestamp: number): boolean {
        const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000 // Obtém o timestamp de 24 horas atrás

        return timestamp >= twentyFourHoursAgo
    }

    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        function handleClickOutside(event: MouseEvent): void {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setShowDropdownId(undefined)
            }
        }
        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside)

        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside)
        }
    })

    const displayOptions = () => {
        return (
            <DivSelect ref={ref}>
                <span
                    className={`select-item ${hardwareData ? '' : 'cursorBlocked'}`}
                    onClick={e => navegateToTrips(e, vehicle)}
                >
                    <i title={i18n('Trips')} className="far fa-route"></i>
                    {i18n('Trips')}
                </span>
                {tripsReportId > 0 && (
                    <span
                        className={`select-item`}
                        onClick={() => {
                            if (hardwareData) {
                                navegateToTripsReport(hardwareData?.collection, vehicle?.imei, tripsReportId)
                            }
                        }}
                    >
                        <i title={i18n('Trips')} className="far fa-file-alt"></i>
                        {i18n('Trips report')}
                    </span>
                )}
            </DivSelect>
        )
    }

    return (
        <StyledVehicles noData={hardwareData?.car_status === undefined} isActive={isActive}>
            <StyleCard onClick={onClick}>
                <StyledRow>
                    <div className="flex-1" style={{ width: '100%' }}>
                        <div className={'header-card'}>
                            <span className="vehicle-header-title">{vehicle?.title}</span>
                            <div style={getCarStatusLegendStyle(hardwareData?.car_status)} className={`statusCard`}>
                                <span>{capitalizeFirstLetter(i18n(hardwareData?.car_status))}</span>
                            </div>
                        </div>
                    </div>
                </StyledRow>

                <DivisorCards />

                <StyledRowClient>
                    <LiveTooltip position="top" content={address || 'N/D'}>
                        <div className="item-row-address">
                            <i className="far fa-map-marker-alt"> </i> {address || 'N/D'}
                        </div>
                    </LiveTooltip>

                    <div className="last-updated">
                        <i className="far fa-history"></i>
                        {hardwareData?.time && (
                            // <span>{`${formatDate(hardwareData?.time)} (${getTimeElapsed(hardwareData?.time)})`}</span>
                            <span>{`${formatDate(hardwareData?.time)}`}</span>
                        )}
                    </div>
                </StyledRowClient>
            </StyleCard>

            <StyledActions
                onClick={() => {
                    if (showDropdownId === vehicle.id) {
                        setShowDropdownId(undefined)
                    } else {
                        setShowDropdownId(vehicle.id)
                    }
                }}
            >
                <i title={i18n('Options')} className="far fa-ellipsis-v"></i>

                {showDropdownId === vehicle?.id && displayOptions()}
            </StyledActions>
        </StyledVehicles>
    )
}

export default React.memo(VehicleCard)

const DivSelect = styled.div`
    border: solid 1px ${({ theme }) => theme['gray-300']};
    width: 263px;
    height: fit-content;
    border-radius: 4px;
    background: ${({ theme }) => theme['gray-0']};
    position: absolute;
    right: 20px;
    top: 60px;
    display: flex;
    flex-direction: column;
    z-index: 2;

    .select-item {
        color: ${({ theme }) => theme['gray-600']};
        display: flex;
        justify-content: flex-start;
        align-items: center;
        width: 100%;
        padding: 16px 12px;
        gap: 8px;
        font-size: 13px;
        font-weight: 400;
    }

    .cursorBlocked {
        cursor: not-allowed;
    }

    .select-item:hover {
        background: ${({ theme }) => theme['blue-200']};
        font-weight: 600;
        color: ${({ theme }) => theme['gray-800']};
        i {
            color: ${({ theme }) => theme['gray-800']};
        }
    }
`

const StyledVehicles = styled.div`
    background: ${({ theme }) => theme['gray-0']};
    min-height: 69px;
    width: 100%;
    position: relative;
    display: grid;
    grid-template-areas: 'CONTENT ACTION';
    grid-template-columns: 90% 1fr;

    border-radius: 3px;
    border-color: ${({ theme, isActive }) => (isActive ? theme['blue-500'] : theme['gray-400'])};
    border-style: solid;
    border-width: ${({ isActive }) => (isActive ? '2px' : '1px')};
    box-sizing: border-box;
    margin-bottom: 8px;
    cursor: pointer;
    width: 512px;

    .divisors-card {
        border-left: solid 0.25px ${({ theme }) => theme['gray-300']};
        width: 100%;
        height: 1px;
        margin: 0;
    }

    .header-card {
        display: flex;
        width: 100%;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
    }

    .statusCard {
        width: fit-content;
        border-radius: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        color: ${({ theme }) => theme['gray-0']};
        padding: 1.5px 6px;

        span {
            font-size: 12px;
            font-weight: 600;
        }
    }

    .text-wrap {
        max-width: 150px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }

    .status-car {
        margin-right: 8px;
        font-size: 10px;
    }

    .status-block {
        margin-left: 5px;
    }

    .vehicle-header-title {
        font-size: 16px;
        width: fit-content;
        font-weight: 600;
        color: ${({ theme }) => theme['gray-800']};
    }

    .vehicle-header-model,
    .vehicle-header-name {
        font-size: 13px;
        font-weight: 400;
        flex: 1;
        color: ${({ theme }) => theme['gray-700']};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
    }

    .item-row-address {
        font-size: 14px;
        font-weight: 400;
        line-height: 120%;
        color: ${({ theme }) => theme['gray-800']};
        width: fit-content;
        max-width: 50%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        .fa-map-marker-alt {
            margin-right: 4px;
            font-size: 12px;
            color: ${({ theme }) => theme['gray-500']};
        }
    }

    .item-row-driver {
        font-size: 12px;

        .fa-user {
            margin-right: 8px;
            color: ${({ theme }) => theme['gray-600']};
        }
    }

    .item-row-separator {
        border: solid 0.25px ${({ theme }) => theme['gray-300']};
        height: 18px;
        margin: 0;
        width: 1;
    }
`

const StyleCard = styled.div`
    padding: 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;

    &:hover {
        background: ${({ theme, noData }) => (noData ? 'none' : theme['blue-200'])};
        cursor: ${({ noData }) => (noData ? 'default' : 'pointer')};
    }
`

const StyledRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-family: 'SourceSansPro-SemiBold', sans-serif;
    gap: 8px;
    height: 33%;
    width: 100%;

    .style-row-details {
        display: flex;
        flex-wrap: wrap;
        width: fit-content;
        max-width: 50%;
    }

    &:first-of-type {
        //margin-bottom: 7px;
    }
`
const StyledRowClient = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 0.5px ${({ theme }) => theme['gray-300']};
    font-family: 'SourceSansPro-Regular', sans-serif;
    //height: 33%;
    //flex: 2;
    width: 100%;

    &:first-of-type {
        margin-bottom: 7px;
    }

    .client-row-signal {
        font-size: 11px;
        width: 16px;
        margin-right: 4px;
    }

    .client-row.multiple-client .client-status:after {
        content: '|';
        margin: 0 8px;
        color: ${({ theme }) => theme['gray-300']};
    }

    .client-row {
        display: flex;
        flex-wrap: wrap;
        flex: 1;
        height: 100%;
        align-items: center;
        justify-content: flex-start;
        text-align: left;
        gap: 4px;

        .operator {
            font-style: italic;
            font-weight: 400;
            font-size: 12px;
            color: ${({ theme }) => theme['gray-600']};
        }

        .client-status {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: center;
            justify-content: flex-start;
            text-align: left;
            gap: 4px;
        }
    }

    .operator-signal-active {
        border-radius: 50%;
        height: 10px;
        width: 10px;
        background: ${({ theme }) => theme['green-700']};
    }
    .operator-signal-deactive {
        border-radius: 50%;
        height: 10px;
        width: 10px;
        border: 0.5px solid ${({ theme }) => theme['gray-300']};
        background: none;
    }

    .text-wrap-client {
        font-size: 11px;
        font-weight: 400;
        color: ${({ theme }) => theme['gray-800']};
        font-family: ${({ theme }) => theme['font-regular']};
    }

    .last-updated {
        i {
            color: ${({ theme }) => theme['gray-500']};
            font-size: 12px;
        }
        font-style: italic;
        font-weight: 400;
        font-size: 12px;
        color: ${({ theme }) => theme['gray-600']};
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 4px;
    }
`
const StyledActions = styled.div`
    grid-area: ACTION;
    display: flex;
    align-items: center;
    justify-content: center;
    border-left: solid 0.25px ${({ theme }) => theme['gray-300']};
    font-size: 14px;
    font-weight: bold;
    margin: 0;
    color: ${({ theme }) => theme['blue-500']};
    &:hover {
        background: ${({ theme, noData }) => (noData ? 'none' : theme['blue-200'])};
        cursor: ${({ noData }) => (noData ? 'default' : 'pointer')};
    }
`
const DivisorCards = styled.div`
    border-top: solid 0.5px ${({ theme }) => theme['gray-300']};
    width: 100%;
`
